import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { camerasApi, pushPreferencesApi } from '@/api/endpoints'
import { PushCategoryPicker } from '@/components/settings/PushCategoryPicker'
import type { Camera, PushCategory, PushCategoryFilter, PushFilters } from '@/types'
import { toast } from '@/lib/toast'

const ORDER: PushCategory[] = ['animal', 'human', 'vehicle', 'audio', 'other']
const DEFAULT_STATE: PushCategoryFilter = { enabled: true, classes: [] }

const stateOf = (filters: Partial<PushFilters> | undefined, cat: PushCategory): PushCategoryFilter =>
  filters?.[cat] ?? DEFAULT_STATE

/**
 * What a category actually yields once the CAMERA filter and the ACCOUNT filter are both
 * applied — the same AND the server performs (camera_filter_blocks, then the per-recipient
 * push_matches). Returns null when the pair can never produce a notification.
 *
 * `classes: []` means "the whole category", so it is the identity element here: intersecting
 * an unrestricted side with a restricted one yields the restricted one, not the empty set.
 */
function effectiveClasses(
  cameraState: PushCategoryFilter,
  accountState: PushCategoryFilter,
): { live: true; classes: string[] } | { live: false; reason: 'disabled' | 'disjoint' } {
  if (!cameraState.enabled || !accountState.enabled) return { live: false, reason: 'disabled' }
  if (cameraState.classes.length === 0) return { live: true, classes: accountState.classes }
  if (accountState.classes.length === 0) return { live: true, classes: cameraState.classes }
  const account = new Set(accountState.classes)
  const both = cameraState.classes.filter((c) => account.has(c))
  return both.length ? { live: true, classes: both } : { live: false, reason: 'disjoint' }
}

export interface CameraAlertsSectionProps {
  camera: Camera
  isSuperuser: boolean
  /** Test seam / caller override. When absent the account filter is fetched. */
  accountFilters?: PushFilters
}

/**
 * CP 159 — per-camera alert categories on the camera sheet.
 *
 * The outcome line is deliberately derived from STORED state (the camera's saved
 * push_filters intersected with the account filter), never from what the picker just
 * wrote. A legacy client can still put a class-narrow filter on a camera through
 * push_class_names, and the only honest way to report that is to read it back.
 *
 * Gated on owner-or-superuser to match the server rule added in CP 159: a shared user
 * gets 403 from PATCH /cameras/{id}/push-settings/, so showing them the control would
 * be offering something that cannot work.
 */
export function CameraAlertsSection({
  camera,
  isSuperuser,
  accountFilters,
}: CameraAlertsSectionProps) {
  const { t } = useTranslation('settings')
  const queryClient = useQueryClient()
  const mayEdit = isSuperuser || camera.is_owner

  const { data: catalog } = useQuery({
    queryKey: ['camera-push-catalog', camera.id],
    queryFn: () => camerasApi.getPushCatalog(camera.id),
    enabled: mayEdit,
  })

  const { data: prefs } = useQuery({
    queryKey: ['push-preferences'],
    queryFn: pushPreferencesApi.get,
    enabled: mayEdit && accountFilters === undefined,
  })

  const [filters, setFilters] = useState<PushFilters>(camera.push_filters ?? ({} as PushFilters))
  const [dirty, setDirty] = useState(false)

  // Never overwrite unsaved edits with a background refetch (same rule as
  // PushCategoriesSection — otherwise another tab saving wipes what is being typed).
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  useEffect(() => {
    if (dirtyRef.current) return
    setFilters(camera.push_filters ?? ({} as PushFilters))
  }, [camera.push_filters])

  const save = useMutation({
    mutationFn: () => camerasApi.updatePushSettings(camera.id, { push_filters: filters }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      setDirty(false)
      toast.success(t('pushCategories.saved'))
    },
    onError: () => toast.error(t('pushCategories.saveFailed')),
  })

  if (!mayEdit) return null

  const account: Partial<PushFilters> = accountFilters ?? prefs?.filters ?? {}
  // Derived from what is SAVED on the camera, not from the in-progress `filters` state.
  const saved: Partial<PushFilters> = camera.push_filters ?? {}

  const live: PushCategory[] = []
  const disjoint: PushCategory[] = []
  for (const cat of ORDER) {
    const outcome = effectiveClasses(stateOf(saved, cat), stateOf(account, cat))
    if (outcome.live) live.push(cat)
    else if (outcome.reason === 'disjoint') disjoint.push(cat)
  }

  const label = (cat: PushCategory) => t(`pushCategories.categories.${cat}`)

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">{t('pushCategories.cameraTitle')}</h3>
        <p className="text-xs text-muted-foreground">{t('pushCategories.cameraDescription')}</p>
        {/* CP 391 — not to be confused with "My phone alerts" (the viewer's own choice) beside it */}
        <p className="text-xs text-muted-foreground">{t('pushCategories.cameraMineHint')}</p>
      </div>

      {disjoint.length > 0 && (
        <p
          role="alert"
          className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
        >
          {t('pushCategories.neverOverlap', { categories: disjoint.map(label).join(', ') })}
        </p>
      )}

      <p data-testid="effective" className="rounded-md bg-muted/50 px-3 py-2 text-xs">
        {live.length > 0
          ? t('pushCategories.effective', { categories: live.map(label).join(', ') })
          : t('pushCategories.nothingEffective')}
      </p>

      {catalog && (
        <PushCategoryPicker
          filters={filters}
          catalog={catalog}
          onChange={(next) => {
            setFilters(next)
            setDirty(true)
          }}
          disabled={save.isPending}
        />
      )}

      {dirty && (
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
        >
          {t('pushCategories.save')}
        </button>
      )}
    </section>
  )
}
